<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <!-- Brand header (desktop rail only; the drawer renders its own header) -->
    <Link
      v-if="variant === 'rail'"
      :href="route('support-panel.dashboard')"
      class="flex h-16 shrink-0 items-center gap-2.5 border-b border-white/5 px-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-300"
      :class="collapsed ? 'justify-center px-0' : ''"
    >
      <span class="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-gradient text-sm font-bold text-white shadow-sm shadow-black/20">J</span>
      <span v-if="!collapsed" class="truncate text-[15px] font-semibold tracking-tight text-white">Support Panel</span>
    </Link>

    <!-- Scrollable nav -->
    <nav class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-4 [scrollbar-width:thin]" aria-label="Support panel">
      <div v-for="(group, gi) in visibleGroups" :key="group.section">
        <!-- Section label (expanded) / hairline separator (collapsed rail) -->
        <p
          v-if="!collapsed"
          class="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-white/60"
          :class="gi === 0 ? 'pt-1' : 'pt-4'"
        >{{ group.section }}</p>
        <div v-else-if="gi > 0" class="mx-2 my-3 h-px bg-white/10" aria-hidden="true"></div>

        <ul role="list" :aria-label="group.section" class="space-y-0.5">
          <li v-for="item in group.items" :key="item.routeName">
            <Link
              :href="route(item.routeName)"
              :aria-current="isActive(item.routeName) ? 'page' : undefined"
              :title="collapsed ? item.label : undefined"
              class="group relative flex items-center rounded-lg text-sm font-medium min-h-[44px] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-300"
              :class="[collapsed ? 'justify-center mx-2 px-0' : 'gap-3 mx-1 px-3', itemStateClass(item)]"
            >
              <!-- Active accent bar -->
              <span
                v-if="isActive(item.routeName)"
                class="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full"
                :class="item.danger ? 'bg-red-400' : 'bg-brand-light'"
                aria-hidden="true"
              ></span>

              <NavIcon :name="item.iconName" class="h-6 w-6 shrink-0" />

              <span class="truncate" :class="collapsed ? 'sr-only' : ''">{{ item.label }}</span>

              <!-- Sensitivity dot (expanded) -->
              <span v-if="item.danger && !collapsed" class="ml-auto h-1.5 w-1.5 rounded-full bg-red-400" aria-hidden="true"></span>
              <!-- Collapsed rail: the visible label is sr-only, so the item name is surfaced via the
                   native `title` attribute on the Link above (a CSS tooltip would be clipped by the
                   nav's overflow-y-auto and never show). -->
            </Link>
          </li>
        </ul>
      </div>
    </nav>

    <!-- Collapse toggle (desktop rail only) -->
    <button
      v-if="variant === 'rail'"
      type="button"
      @click="emit('toggle-collapse')"
      :aria-pressed="collapsed"
      :aria-label="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      class="flex h-12 shrink-0 items-center gap-3 border-t border-white/5 px-4 text-white/50 hover:bg-white/5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-300"
      :class="collapsed ? 'justify-center px-0' : ''"
    >
      <NavIcon
        name="chevron-double-left"
        class="h-5 w-5 shrink-0 transition-transform motion-reduce:transition-none"
        :class="collapsed ? 'rotate-180' : ''"
      />
      <span v-if="!collapsed" class="text-sm">Collapse</span>
    </button>
  </div>
</template>

<script setup>
import { Link } from '@inertiajs/vue3';
import NavIcon from './NavIcon.vue';
import { useSupportNav } from '../useSupportNav.js';

defineProps({
  collapsed: { type: Boolean, default: false },
  variant: { type: String, default: 'rail' }, // 'rail' (desktop) | 'drawer' (mobile)
});
const emit = defineEmits(['toggle-collapse']);

const { visibleGroups, isActive } = useSupportNav();

// Returns one complete, literal class string per state so Tailwind's content
// scanner sees every variant (no runtime concatenation of class fragments).
function itemStateClass(item) {
  if (item.danger) {
    return isActive(item.routeName)
      ? 'bg-red-500/15 text-red-200'
      : 'text-red-300/80 hover:bg-red-500/10 hover:text-red-200';
  }
  return isActive(item.routeName)
    ? 'bg-teal-400/10 text-brand-light'
    : 'text-white/70 hover:bg-white/5 hover:text-white';
}
</script>
